import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube } from "lucide-react";
import { motion } from "framer-motion";
import logo from "@/assets/logo.png";

export default function Footer() {
  return (
    <motion.footer
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.9, ease: "easeOut" }}
      className="border-t border-white/5 bg-hblack pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
        {/* Column 1: Brand Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-4"
        >
          <div className="flex items-center gap-3 mb-6">
            <img src={logo} alt="Hammam Logo" className="h-12 w-auto object-contain" />
            <span className="font-heading text-2xl text-white tracking-wider">Hammam Men Spa</span>
          </div>
          <p className="text-hsecondary text-sm leading-relaxed mb-3">
            A sanctuary of quiet luxury — traditional hammam rituals reimagined for the modern gentleman.
          </p>
          <p className="text-hsecondary/80 text-sm leading-relaxed">
            Experience premium male grooming, therapeutic massages, and luxury wellness at India's finest men-only spa.
          </p>
        </motion.div>

        {/* Column 2: Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="lg:col-span-2"
        >
          <div className="label-eyebrow mb-4 text-sm tracking-widest text-hgold">Quick Links</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/services" className="text-white/70 hover:text-hgold transition-colors nav-link-hover inline-block">Services</Link></li>
            <li><Link to="/membership" className="text-white/70 hover:text-hgold transition-colors nav-link-hover inline-block">Membership</Link></li>
            <li><Link to="/doorstep" className="text-white/70 hover:text-hgold transition-colors nav-link-hover inline-block">Doorstep</Link></li>
            <li><Link to="/about" className="text-white/70 hover:text-hgold transition-colors nav-link-hover inline-block">About</Link></li>
            <li><Link to="/branches" className="text-white/70 hover:text-hgold transition-colors nav-link-hover inline-block">Branches</Link></li>
            <li><Link to="/contact" className="text-white/70 hover:text-hgold transition-colors nav-link-hover inline-block">Contact</Link></li>
            <li><Link to="/privacy-policy" className="text-white/70 hover:text-hgold transition-colors nav-link-hover inline-block">Privacy Policy</Link></li>
          </ul>
        </motion.div>

        {/* Column 3: Follow Us */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="lg:col-span-3"
        >
          <div className="label-eyebrow mb-4 text-sm tracking-widest text-hgold">Follow Us</div>
          <p className="text-hsecondary text-sm leading-relaxed mb-4">
            Connect with us on social media for updates, wellness tips, and exclusive offers.
          </p>
          <div className="flex flex-col gap-2.5">
            <a
              href="https://www.instagram.com/hammammensspa.india/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-orange-500/10 border border-pink-700/30 text-pink-400 px-4 py-2.5 rounded-lg transition-all duration-300 hover:scale-105 hover:border-pink-500 hover:shadow-lg hover:shadow-pink-950/40 text-xs font-semibold uppercase tracking-wider w-44 shadow-sm group"
            >
              <Instagram size={16} className="text-pink-400 group-hover:scale-110 transition-transform" /> Instagram
            </a>
            <a
              href="https://www.facebook.com/gspaindia/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="inline-flex items-center gap-3 bg-blue-600/10 border border-blue-500/30 text-blue-400 px-4 py-2.5 rounded-lg transition-all duration-300 hover:scale-105 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-950/40 text-xs font-semibold uppercase tracking-wider w-44 shadow-sm group"
            >
              <Facebook size={16} className="text-blue-400 group-hover:scale-110 transition-transform" /> Facebook
            </a>
            <a
              href="https://www.youtube.com/@hammammensspa1575"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="inline-flex items-center gap-3 bg-red-600/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-lg transition-all duration-300 hover:scale-105 hover:border-red-500 hover:shadow-lg hover:shadow-red-950/40 text-xs font-semibold uppercase tracking-wider w-44 shadow-sm group"
            >
              <Youtube size={16} className="text-red-400 group-hover:scale-110 transition-transform" /> YouTube
            </a>
          </div>
        </motion.div>

        {/* Column 4: Reach Us */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="lg:col-span-3"
        >
          <div className="label-eyebrow mb-4 text-sm tracking-widest text-hgold">Reach Us</div>
          <ul className="space-y-4 text-sm text-white/70">
            <li className="flex items-start gap-2.5">
              <Phone size={16} className="mt-1 text-hgold shrink-0" />
              <div>
                <div className="text-xs text-white/40 uppercase tracking-widest mb-1">Contact</div>
                <div className="flex flex-col gap-1 text-white/90 font-medium text-sm">
                  <a href="tel:+919560656913" className="hover:text-hgold transition-colors nav-link-hover inline-block">+91 9560656913</a>
                  <a href="tel:+919560656913" className="hover:text-hgold transition-colors nav-link-hover inline-block">+91 9560656913</a>
                </div>
              </div>
            </li>
            <li className="flex items-start gap-2.5">
              <Mail size={16} className="mt-1 text-hgold shrink-0" />
              <div>
                <div className="text-xs text-white/40 uppercase tracking-widest mb-1">Email</div>
                <a href="mailto:info.hammammensspa@gmail.com" className="text-white/90 text-sm hover:text-hgold transition-colors nav-link-hover inline-block whitespace-nowrap">
                  info.hammammensspa@gmail.com
                </a>
              </div>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin size={16} className="mt-1 text-hgold shrink-0" />
              <div>
                <div className="text-xs text-white/40 uppercase tracking-widest mb-1">Locations</div>
                <div className="flex flex-col gap-1 text-white/90 text-sm">
                  <a href="https://maps.app.goo.gl/65Dwm9Nt6GAhpuoL6" target="_blank" rel="noopener noreferrer" className="hover:text-hgold transition-colors nav-link-hover inline-block">Lajpat Nagar</a>
                  <a href="https://maps.app.goo.gl/5ChSfibhJxcBDTvH6" target="_blank" rel="noopener noreferrer" className="hover:text-hgold transition-colors nav-link-hover inline-block">Rajouri Garden</a>
                  <a href="https://maps.app.goo.gl/oT8hJtohHm2YajVy6" target="_blank" rel="noopener noreferrer" className="hover:text-hgold transition-colors nav-link-hover inline-block">G 69 Gurugram</a>
                </div>
              </div>
            </li>
          </ul>
        </motion.div>
      </div>

      <div className="hammam-divider max-w-7xl mx-auto my-10" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center text-sm text-hmuted">
        <div>© {new Date().getFullYear()} Hammam Mens Spa. All rights reserved.</div>
      </div>
    </motion.footer>
  );
}
